/**
 * Literal Equations generator (solve a formula for a specified variable).
 *
 * Rearranging a formula symbolically has no single canonical text form, and a
 * general symbolic-equivalence checker is more complexity than V1 needs. So
 * this topic is a deterministic multiple-choice: present a known formula, ask
 * the student to solve for a variable, and offer the correct rearrangement plus
 * carefully constructed distractors (common sign/operation errors). Grading is
 * exact choice matching.
 *
 * The correct answers are hand-verified formula identities from a fixed table,
 * so there is no runtime symbolic manipulation to get wrong.
 */

import { Rng } from "../engine/rng";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Choice,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "literal-equations" as const;
const VERSION = "1.0.0";

interface WorkStep {
  readonly description: string;
  readonly latex: string;
}

interface FormulaItem {
  readonly family: string;
  readonly formulaLatex: string;
  readonly solveFor: string;
  /** The correct rearrangement (LaTeX). */
  readonly correct: string;
  /** Distractors: plausible-but-wrong rearrangements (LaTeX). */
  readonly distractors: readonly string[];
  /** Worked rearrangement steps, ending at `correct`. */
  readonly work: readonly WorkStep[];
  readonly difficulty: Difficulty;
}

// Each entry is a verified identity. Distractors encode common errors
// (wrong inverse operation, dropped/misplaced term, sign error).
const FORMULAS: readonly FormulaItem[] = [
  {
    family: "area-rect",
    formulaLatex: "A = \\ell w",
    solveFor: "w",
    correct: "w = \\frac{A}{\\ell}",
    distractors: ["w = A\\ell", "w = A - \\ell", "w = \\frac{\\ell}{A}"],
    work: [
      { description: "w is multiplied by \\ell. Undo that by dividing both sides by \\ell.", latex: "\\frac{A}{\\ell} = \\frac{\\ell w}{\\ell}" },
      { description: "On the right, \\ell cancels, leaving w.", latex: "w = \\frac{A}{\\ell}" },
    ],
    difficulty: "easy",
  },
  {
    family: "perimeter-rect",
    formulaLatex: "P = 2\\ell + 2w",
    solveFor: "w",
    correct: "w = \\frac{P - 2\\ell}{2}",
    distractors: ["w = P - 2\\ell", "w = \\frac{P}{2} - \\ell", "w = 2P - \\ell"],
    work: [
      { description: "First get the 2w term alone: subtract 2\\ell from both sides.", latex: "P - 2\\ell = 2w" },
      { description: "w is multiplied by 2. Divide both sides by 2.", latex: "w = \\frac{P - 2\\ell}{2}" },
    ],
    difficulty: "standard",
  },
  {
    family: "distance",
    formulaLatex: "d = rt",
    solveFor: "t",
    correct: "t = \\frac{d}{r}",
    distractors: ["t = dr", "t = d - r", "t = \\frac{r}{d}"],
    work: [
      { description: "t is multiplied by r. Do the opposite: divide both sides by r.", latex: "\\frac{d}{r} = \\frac{rt}{r}" },
      { description: "On the right, r divided by r is 1, so it cancels and leaves t.", latex: "t = \\frac{d}{r}" },
    ],
    difficulty: "easy",
  },
  {
    family: "simple-interest",
    formulaLatex: "I = Prt",
    solveFor: "r",
    correct: "r = \\frac{I}{Pt}",
    distractors: ["r = \\frac{I}{P} - t", "r = IPt", "r = \\frac{Pt}{I}"],
    work: [
      { description: "r is multiplied by both P and t. Divide both sides by that whole product, Pt.", latex: "\\frac{I}{Pt} = \\frac{Prt}{Pt}" },
      { description: "On the right, Pt cancels, leaving r.", latex: "r = \\frac{I}{Pt}" },
    ],
    difficulty: "standard",
  },
  {
    family: "slope-intercept",
    formulaLatex: "y = mx + b",
    solveFor: "x",
    correct: "x = \\frac{y - b}{m}",
    distractors: ["x = \\frac{y}{m} - b", "x = y - b - m", "x = \\frac{y + b}{m}"],
    work: [
      { description: "Get the mx term alone: subtract b from both sides.", latex: "y - b = mx" },
      { description: "x is multiplied by m. Divide the whole left side by m.", latex: "x = \\frac{y - b}{m}" },
    ],
    difficulty: "standard",
  },
  {
    family: "temperature",
    formulaLatex: "F = \\frac{9}{5}C + 32",
    solveFor: "C",
    correct: "C = \\frac{5(F - 32)}{9}",
    distractors: ["C = \\frac{9(F - 32)}{5}", "C = \\frac{5F}{9} - 32", "C = \\frac{5}{9}F + 32"],
    work: [
      { description: "Get the C term alone: subtract 32 from both sides.", latex: "F - 32 = \\frac{9}{5}C" },
      { description: "C is multiplied by \\tfrac{9}{5}. Multiply both sides by its reciprocal, \\tfrac{5}{9}.", latex: "\\frac{5}{9}(F - 32) = C" },
      { description: "That is the same as the fraction below.", latex: "C = \\frac{5(F - 32)}{9}" },
    ],
    difficulty: "hard",
  },
  {
    family: "triangle-area",
    formulaLatex: "A = \\frac{1}{2}bh",
    solveFor: "h",
    correct: "h = \\frac{2A}{b}",
    distractors: ["h = \\frac{A}{2b}", "h = 2Ab", "h = \\frac{b}{2A}"],
    work: [
      { description: "Clear the fraction: multiply both sides by 2.", latex: "2A = bh" },
      { description: "h is multiplied by b. Divide both sides by b.", latex: "h = \\frac{2A}{b}" },
    ],
    difficulty: "standard",
  },
  {
    family: "kinematics",
    formulaLatex: "v = v_0 + at",
    solveFor: "t",
    correct: "t = \\frac{v - v_0}{a}",
    distractors: ["t = \\frac{v}{a} - v_0", "t = v - v_0 - a", "t = \\frac{v + v_0}{a}"],
    work: [
      { description: "Get the at term alone: subtract v_0 from both sides.", latex: "v - v_0 = at" },
      { description: "t is multiplied by a. Divide the whole left side by a.", latex: "t = \\frac{v - v_0}{a}" },
    ],
    difficulty: "hard",
  },
  {
    family: "cylinder-volume",
    formulaLatex: "V = \\pi r^2 h",
    solveFor: "h",
    correct: "h = \\frac{V}{\\pi r^2}",
    distractors: ["h = \\frac{\\pi r^2}{V}", "h = V - \\pi r^2", "h = V\\pi r^2"],
    work: [
      { description: "h is multiplied by \\pi r^2. Divide both sides by that whole quantity.", latex: "\\frac{V}{\\pi r^2} = \\frac{\\pi r^2 h}{\\pi r^2}" },
      { description: "On the right, \\pi r^2 cancels, leaving h.", latex: "h = \\frac{V}{\\pi r^2}" },
    ],
    difficulty: "challenge",
  },
  {
    family: "two-point-mid",
    formulaLatex: "M = \\frac{a + b}{2}",
    solveFor: "b",
    correct: "b = 2M - a",
    distractors: ["b = \\frac{M - a}{2}", "b = 2M + a", "b = M - 2a"],
    work: [
      { description: "Clear the fraction: multiply both sides by 2.", latex: "2M = a + b" },
      { description: "Get b alone: subtract a from both sides.", latex: "b = 2M - a" },
    ],
    difficulty: "hard",
  },
];

function eligible(difficulty: Difficulty): readonly FormulaItem[] {
  // Include the level and everything below it, so easier levels have variety
  // and harder levels can still surface simpler-but-valid rearrangements.
  const order: Difficulty[] = ["easy", "standard", "hard", "challenge"];
  const cap = order.indexOf(difficulty);
  const items = FORMULAS.filter((f) => order.indexOf(f.difficulty) <= cap);
  // For hard/challenge, prefer items at or above standard for appropriate rigor.
  if (difficulty === "hard" || difficulty === "challenge") {
    const rigorous = items.filter((f) => order.indexOf(f.difficulty) >= 1);
    return rigorous.length >= 2 ? rigorous : items;
  }
  return items;
}

export class LiteralEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    const rng = new Rng(ctx.seed);
    const pool = eligible(ctx.difficulty);
    const item = rng.pick(pool);

    // Build shuffled choices; the correct one gets a stable id.
    const correctChoice: Choice = { id: "correct", latex: item.correct };
    const distractorChoices: Choice[] = item.distractors.map((d, i) => ({
      id: `d${i}`,
      latex: d,
    }));
    const choices = rng.shuffle([correctChoice, ...distractorChoices]);

    return {
      id: `${TOPIC}-${ctx.seed}-${item.family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family: item.family,
      difficulty: ctx.difficulty,
      prompt: {
        text: `Solve ${item.formulaLatex} for ${item.solveFor}`,
        latex: `\\text{Solve } ${item.formulaLatex} \\text{ for } ${item.solveFor}`,
      },
      answerType: "choice",
      answer: { kind: "choice", value: "correct" },
      choices,
      steps: item.work.map((w) => ({ description: w.description, latex: w.latex })),
      hints: [
        { level: 1, text: `Undo the operations attached to ${item.solveFor}, one at a time.` },
        { level: 2, text: "Apply the same inverse operation to both sides." },
      ],
      subskills: topic.subskills,
    };
  }

  signature(q: Question): QuestionSignature {
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure: q.family };
  }
}

export const literalEquations = new LiteralEquationsGenerator();
